import React, { useState, useMemo } from 'react';
import { Coin } from '../../data/coins';
import {
  DENOMINATION_TABS,
  DenominationKey,
  getStandardCountries,
  getCommemorativeCountries,
  getStandardYears,
  getCommemorativeYears,
  buildStandardMatrix,
  buildCommemorativeMatrix
} from '../lib/albumData';
import { AlbumSlot } from './AlbumSlot';

interface AlbumGridViewProps {
  userAlbum: Record<string, number>;
  onHoverCoin: (coin: Coin, count: number, e: React.MouseEvent<HTMLElement>) => void;
  onLeaveCoin: () => void;
  onClickCoin: (coin: Coin, count: number, e: React.MouseEvent<HTMLElement>) => void;
}

export const AlbumGridView: React.FC<AlbumGridViewProps> = ({
  userAlbum,
  onHoverCoin,
  onLeaveCoin,
  onClickCoin
}) => {
  const [activeDenom, setActiveDenom] = useState<DenominationKey>('2euro');
  const [subTab, setSubTab] = useState<'standard' | 'commemorative'>('standard');

  const standardCountries = useMemo(() => getStandardCountries(activeDenom), [activeDenom]);
  const standardYears = useMemo(() => getStandardYears(activeDenom), [activeDenom]);
  const standardMatrix = useMemo(() => buildStandardMatrix(activeDenom), [activeDenom]);

  const commemorativeCountries = useMemo(() => getCommemorativeCountries(), []);
  const commemorativeYears = useMemo(() => getCommemorativeYears(), []);
  const commemorativeMatrix = useMemo(() => buildCommemorativeMatrix(), []);

  const is2Euro = activeDenom === '2euro';
  const effectiveSubTab = is2Euro ? subTab : 'standard';

  const countries = effectiveSubTab === 'standard' ? standardCountries : commemorativeCountries;
  const years = effectiveSubTab === 'standard' ? standardYears : commemorativeYears;

  const stats = useMemo(() => {
    let total = 0;
    let owned = 0;

    if (effectiveSubTab === 'standard') {
      for (const country of standardCountries) {
        const countryMap = standardMatrix.get(country);
        if (!countryMap) continue;
        for (const coins of countryMap.values()) {
          for (const coin of coins) {
            total++;
            if ((userAlbum[coin.id] || 0) > 0) owned++;
          }
        }
      }
    } else {
      for (const country of commemorativeCountries) {
        const countryMap = commemorativeMatrix.get(country);
        if (!countryMap) continue;
        for (const coins of countryMap.values()) {
          for (const coin of coins) {
            total++;
            if ((userAlbum[coin.id] || 0) > 0) owned++;
          }
        }
      }
    }

    const pct = total > 0 ? ((owned / total) * 100).toFixed(1) : '0.0';
    return { total, owned, pct };
  }, [effectiveSubTab, standardCountries, commemorativeCountries, standardMatrix, commemorativeMatrix, userAlbum]);

  return (
    <div className="album-grid-view">
      {/* Top Denomination Selector Tabs (2€ down to 1¢) */}
      <div className="album-denom-tabs-row">
        {DENOMINATION_TABS.map(tab => (
          <button
            key={tab.key}
            className={`denom-tab-btn ${activeDenom === tab.key ? 'active' : ''}`}
            onClick={() => {
              setActiveDenom(tab.key);
              if (tab.key !== '2euro') {
                setSubTab('standard');
              }
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Sub-tabs row: Standard vs Commemorative (ONLY visible for 2 Euros) */}
      <div className="album-subtabs-row">
        {is2Euro ? (
          <div className="album-subtabs-pill-group">
            <button
              className={`subtab-btn ${subTab === 'standard' ? 'active' : ''}`}
              onClick={() => setSubTab('standard')}
            >
              Standard
            </button>
            <button
              className={`subtab-btn ${subTab === 'commemorative' ? 'active' : ''}`}
              onClick={() => setSubTab('commemorative')}
            >
              Commemorative
            </button>
          </div>
        ) : (
          <div className="album-subtabs-pill-group">
            <span className="subtab-active-label">
              Standard
            </span>
          </div>
        )}

        <div className="subtab-quick-stats">
          <span>Progress: <strong>{stats.owned}</strong> / {stats.total} ({stats.pct}%)</span>
        </div>
      </div>

      {/* Grid Table */}
      <div className="album-table-wrapper">
        <table className={`album-matrix-table ${effectiveSubTab === 'commemorative' ? 'is-commemorative' : 'is-standard'}`}>
          <thead>
            <tr>
              <th className="sticky-country-th">Country</th>
              {years.map(yr => (
                <th key={yr} className="year-th">{yr}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {countries.map(country => {
              const countryStdMap = effectiveSubTab === 'standard' ? standardMatrix.get(country) : null;
              const countryCommMap = effectiveSubTab === 'commemorative' ? commemorativeMatrix.get(country) : null;

              return (
                <tr key={country} className="country-row">
                  <td className="sticky-country-td">{country}</td>

                  {years.map(year => {
                    const coins = (effectiveSubTab === 'standard' ? countryStdMap?.get(year) : countryCommMap?.get(year)) || [];
                    if (coins.length === 0) {
                      return (
                        <td key={year} className={`coin-cell ${effectiveSubTab === 'commemorative' ? 'comm-cell' : ''} empty-slot`}>
                          <span className="slot-empty-dash">—</span>
                        </td>
                      );
                    }

                    return (
                      <td key={year} className={`coin-cell ${effectiveSubTab === 'commemorative' ? 'comm-cell' : ''}`}>
                        <div className="comm-cell-coins-flex">
                          {coins.map(coin => {
                            const count = userAlbum[coin.id] || 0;

                            return (
                              <div key={coin.id}>
                                <AlbumSlot
                                  coin={coin}
                                  count={count}
                                  size={coins.length > 1 ? 'small' : 'normal'}
                                  onHover={onHoverCoin}
                                  onLeave={onLeaveCoin}
                                  onClick={onClickCoin}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
